import {
  DIN_PAPER_SIZES,
  effectivePageSizes,
  isDinPaperSize,
  type DinPaperSize,
} from "@/lib/paperSize";

export type DocumentPaperState = {
  detected: Array<DinPaperSize | null>;
  overrides: Record<number, DinPaperSize>;
};

const STORAGE_PREFIX = "eh-doc-paper:";

export function documentPaperKey(orderId: number, pack: string, tabKey: string): string {
  return `${STORAGE_PREFIX}${orderId}:${pack}:${tabKey}`;
}

function parseState(raw: unknown): DocumentPaperState | null {
  if (!raw || typeof raw !== "object") return null;
  const detectedRaw = (raw as { detected?: unknown }).detected;
  const overridesRaw = (raw as { overrides?: unknown }).overrides;
  if (!Array.isArray(detectedRaw)) return null;
  const detected = detectedRaw.map((value) => (isDinPaperSize(value) ? value : null));
  const overrides: Record<number, DinPaperSize> = {};
  if (overridesRaw && typeof overridesRaw === "object") {
    for (const [key, value] of Object.entries(overridesRaw)) {
      const page = Number(key);
      if (Number.isInteger(page) && page > 0 && isDinPaperSize(value)) {
        overrides[page] = value;
      }
    }
  }
  return { detected, overrides };
}

export function loadDocumentPaperState(docKey: string): DocumentPaperState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(docKey);
    if (!raw) return null;
    return parseState(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveDocumentPaperState(docKey: string, state: DocumentPaperState): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(docKey, JSON.stringify(state));
  } catch {
    /* Quota / private mode */
  }
}

export function setPagePaperOverride(
  state: DocumentPaperState,
  pageNum: number,
  size: DinPaperSize | null,
): DocumentPaperState {
  const overrides = { ...state.overrides };
  const detected = state.detected[pageNum - 1] ?? null;
  if (!size || size === detected) {
    delete overrides[pageNum];
  } else {
    overrides[pageNum] = size;
  }
  return { ...state, overrides };
}

export function paperSizeSelectOptions(): Array<{ value: DinPaperSize; label: string }> {
  return DIN_PAPER_SIZES.map((size) => ({ value: size, label: `DIN ${size}` }));
}

/** JSON-Array der wirksamen Seitenformate, oder null wenn noch nichts erkannt wurde. */
export function storedPagePaperSizesJson(docKey: string): string | null {
  const state = loadDocumentPaperState(docKey);
  if (!state) return null;
  if (!state.detected.length && !Object.keys(state.overrides).length) return null;
  const sizes = effectivePageSizes(state.detected, state.overrides);
  if (!sizes.some((size) => size != null)) return null;
  return JSON.stringify(sizes);
}

export function storedPaperSummary(docKey: string): string | null {
  const state = loadDocumentPaperState(docKey);
  if (!state) return null;
  const sizes = effectivePageSizes(state.detected, state.overrides).filter(
    (size): size is DinPaperSize => size != null,
  );
  if (!sizes.length) return null;
  return [...new Set(sizes)].map((size) => `DIN ${size}`).join(", ");
}
